import { AssetType } from '@amzn/fire-tv-campaign-automation-service';

/**
 * Types of layers that can be rendered in a preview
 * - BACKGROUND: Background image with transforms
 * - VIDEO: Video trailer with transforms
 * - GRADIENT: Gradient overlay
 * - LOGO: Logo image with positioning
 * - SHELL: Device shell overlay image
 * - TEXT: Text layer with wrapping
 */
export enum LayerType {
  BACKGROUND = 'background',
  VIDEO = 'video',
  GRADIENT = 'gradient',
  LOGO = 'logo',
  SHELL = 'shell',
  TEXT = 'text',
}

/**
 * Transform types for background images
 * - RESIZE: Resize to exact dimensions (supports upscaling)
 * - CENTER_CROP: Scale to fill, crop overflow, with horizontal shift
 * - LEFT_FOCUS: Scale to fill with left focus (shiftFactor: 0.15)
 * - RIGHT_FOCUS: Scale to fill with right focus (shiftFactor: 0.85)
 */
export enum BackgroundTransformType {
  RESIZE = 'resize',
  CENTER_CROP = 'centerCrop',
  LEFT_FOCUS = 'leftFocus',
  RIGHT_FOCUS = 'rightFocus',
}

/**
 * Transform types for logo positioning
 * - FIXED: Exact coordinates and dimensions
 * - FIT_WITHIN: Scale to fit within bounds, maintain aspect ratio
 * - SCALE: Scale by factor with alignment
 */
export enum LogoTransformType {
  FIXED = 'fixed',
  FIT_WITHIN = 'fitWithin',
  SCALE = 'scale',
}

/**
 * Gradient direction options
 */
export enum GradientDirection {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

/**
 * Horizontal alignment options
 */
export enum HorizontalAlign {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

/**
 * Vertical alignment options
 */
export enum VerticalAlign {
  TOP = 'top',
  CENTER = 'center',
  BOTTOM = 'bottom',
}

/**
 * Width and height dimensions
 */
export interface Dimensions {
  width: number;
  height: number;
}

/**
 * Rectangular bounds with position and dimensions
 */
export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Position with numeric coordinates
 */
export interface Position {
  x: number;
  y: number;
}

/**
 * Result of applying a transform - defines position and dimensions on canvas
 */
export interface TransformResult {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Resizes image to exact dimensions
 * @example { type: RESIZE, width: 2410, height: 904, x: 0, y: 0 }
 */
export interface ResizeTransform {
  type: BackgroundTransformType.RESIZE;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

/**
 * Scales image to fill target area, crops overflow
 * @param shiftFactor - Horizontal position (0=left, 0.5=center, 1=right)
 * @param x, y - Offset position on canvas
 * @param width, height - Target area dimensions (defaults to canvas size)
 * @example { type: CENTER_CROP, shiftFactor: 0.85, x: 10, y: 236, width: 580, height: 260 }
 */
export interface CenterCropTransform {
  type: BackgroundTransformType.CENTER_CROP;
  shiftFactor?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

/**
 * Scales to fill canvas, positions with left focus (shiftFactor: 0.15)
 * @example { type: LEFT_FOCUS, shiftFactor: 0.2 }
 */
export interface LeftFocusTransform {
  type: BackgroundTransformType.LEFT_FOCUS;
  shiftFactor?: number;
}

/**
 * Scales to fill canvas, positions with right focus (shiftFactor: 0.85)
 * @example { type: RIGHT_FOCUS, shiftFactor: 0.9 }
 */
export interface RightFocusTransform {
  type: BackgroundTransformType.RIGHT_FOCUS;
  shiftFactor?: number;
}

/**
 * Union type of all background transform types
 */
export type BackgroundTransform =
  | ResizeTransform
  | CenterCropTransform
  | LeftFocusTransform
  | RightFocusTransform;

/**
 * Positions logo at exact coordinates with fixed dimensions
 * @example { type: FIXED, x: 100, y: 200, width: 300, height: 150 }
 */
export interface FixedTransform {
  type: LogoTransformType.FIXED;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Scales logo to fit within bounds, maintains aspect ratio
 * @example { type: FIT_WITHIN, maxWidth: 247, maxHeight: 100, position: { x: 60, y: TOP, offsetY: 286 } }
 */
export interface FitWithinTransform {
  type: LogoTransformType.FIT_WITHIN;
  maxWidth: number;
  maxHeight: number;
  position: Position;
}

/**
 * Scales logo by factor, positions using alignment
 * @example { type: SCALE, scale: 0.5, position: { x: CENTER, y: TOP, offsetY: 50 } }
 */
export interface ScaleTransform {
  type: LogoTransformType.SCALE;
  scale: number;
  position: Position;
}

/**
 * Union type of all logo transform types
 */
export type LogoTransform = FixedTransform | FitWithinTransform | ScaleTransform;

/**
 * Color stop for gradient rendering
 */
export interface GradientStop {
  position: number;
  color: string;
}

/**
 * Base layer interface
 */
export interface BaseLayer {
  type: LayerType;
}

/**
 * Background image layer with transform
 * @example { type: BACKGROUND, transform: { type: CENTER_CROP, shiftFactor: 0.85 } }
 */
export interface BackgroundLayer extends BaseLayer {
  type: LayerType.BACKGROUND;
  transform: BackgroundTransform;
}

/**
 * Video layer (uses video trailer asset)
 * @example { type: VIDEO, transform: { type: CENTER_CROP } }
 */
export interface VideoLayer extends BaseLayer {
  type: LayerType.VIDEO;
  transform: BackgroundTransform;
}

/**
 * Gradient overlay layer
 * @example { type: GRADIENT, direction: GradientDirection.HORIZONTAL, colorStops: [{ position: 0, color: 'rgba(0,0,0,0.8)' }, { position: 1, color: 'rgba(0,0,0,0)' }], bounds: { x: 10, y: 236, width: 290, height: 260 } }
 */
export interface GradientLayer extends BaseLayer {
  type: LayerType.GRADIENT;
  direction: GradientDirection;
  colorStops: GradientStop[];
  bounds: Bounds;
}

/**
 * Logo image layer with transform
 * @example { type: LOGO, transform: { type: FIT_WITHIN, maxWidth: 247, maxHeight: 100, position: { x: 60, y: TOP, offsetY: 286 } } }
 */
export interface LogoLayer extends BaseLayer {
  type: LayerType.LOGO;
  transform: LogoTransform;
}

/**
 * Device shell overlay image
 * @example { type: SHELL, src: shell7InchPortrait }
 */
export interface ShellLayer extends BaseLayer {
  type: LayerType.SHELL;
  src: string;
}

/**
 * Text layer with wrapping and alignment
 * @example { type: TEXT, fontSize: 24, fontFamily: 'Amazon Ember', color: '#FFFFFF', bounds: { x: 60, y: 400, width: 480, height: 100 }, align: LEFT, verticalAlign: TOP }
 */
export interface TextLayer extends BaseLayer {
  type: LayerType.TEXT;
  fontSize: number;
  fontFamily: string;
  color: string;
  maxWidth: number;
  maxLines: number;
  position: Position;
  lineHeight?: number;
}

/**
 * Union type of all layer types
 */
export type Layer =
  | BackgroundLayer
  | VideoLayer
  | GradientLayer
  | LogoLayer
  | ShellLayer
  | TextLayer;

/**
 * Complete preview configuration for a device state/variant
 * Defines canvas dimensions, layers to render, and UI metadata
 *
 * @property dimensions - Canvas width and height
 * @property clip - Optional clipping path for rounded corners
 * @property layers - Ordered array of layers to render (background, logo, gradient, etc.)
 * @property assetRequirements - Assets needed to render this state
 * @property label - Display name for UI controls (e.g., "Selected", "10\" Landscape")
 * @property altText - Placeholder text when assets are missing
 *
 * @example FireTV Selected State
 * {
 *   label: 'Selected',
 *   altText: 'Please upload both background image and logo',
 *   dimensions: { width: 1920, height: 1080 },
 *   layers: [
 *     { type: BACKGROUND, transform: { type: RESIZE, width: 2410, height: 904 } },
 *     { type: LOGO, transform: { type: FIXED, x: 104, y: 270, width: 640, height: 260 } },
 *     { type: SHELL, src: selectedOverlay }
 *   ],
 *   assetRequirements: [BACKGROUND_IMAGE, LOGO]
 * }
 *
 * @example FireTablet 7" Portrait
 * {
 *   label: '7" Portrait',
 *   altText: 'Please upload both background image and logo to preview',
 *   dimensions: { width: 600, height: 1024 },
 *   layers: [
 *     { type: BACKGROUND, transform: { type: CENTER_CROP, shiftFactor: 0.85, x: 10, y: 236, width: 580, height: 260 } },
 *     { type: LOGO, transform: { type: FIT_WITHIN, maxWidth: 247, maxHeight: 100, position: { x: 60, y: TOP, offsetY: 286 } } },
 *     { type: SHELL, src: shell7InchPortrait }
 *   ],
 *   assetRequirements: [BACKGROUND_IMAGE, LOGO]
 * }
 */
export interface PreviewStateConfig {
  dimensions: Dimensions;
  layers: Layer[];
  assetRequirements: AssetType[];
  label?: string;
  altText?: string;
}

/**
 * Canvas utility types
 */

/**
 * Result of center crop calculation
 */
export interface CenterCropResult {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Result of fit within calculation
 */
export interface FitWithinResult {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Options for fit within calculation
 */
export interface FitWithinOptions {
  positionX?: number;
  positionY?: number;
}

/**
 * Text alignment options
 */
export type Alignment = 'start' | 'center' | 'end';

/**
 * Options for text wrapping
 */
export interface WrapTextOptions {
  text: string;
  maxWidth: number;
  maxLines: number;
  ctx: CanvasRenderingContext2D;
}

/**
 * Result of text wrapping with truncation flag
 */
export interface WrapTextResult {
  lines: string[];
  isTruncated: boolean;
}

/**
 * Options for resolving text position
 */
export interface ResolveTextPositionOptions {
  positionX: number;
  positionY: number;
}
